import React, { Fragment, useMemo, useState } from 'react';
import AppTable from '../../../../constants/AppTable';
import { LoadingSpinner } from '../../../../constants/AppLoading';
import DialogBox from '../../../../constants/DialogBox';
import { Grid } from '@mui/material';
import InputTextBox from '../../../../constants/InputTextBox';
import { readerEditValidationSchema, readerValidationSchema, toleValidationSchema } from '../../../../helpers/validation';
import { useFormik } from 'formik';
import NepaliInputBox from '../../../../constants/NepaliInputBox';
import { handleNepaliInputChange } from '../../../../helpers/handleChange';
import { EndBaseLine } from '../../../../constants/AppSpace';
import { Button } from '../../../../constants/Button';
import { putDataToTheServer } from '../../../../helpers/api';
import AppURL from '../../../../constants/AppURL';
import { ToastSuccess, ToastWarning } from '../../../../constants/Toast';
import { ProductMaster, Reader } from '../../../../helpers/models/Models';
import DefaultSelectInputBox from '../../../../constants/DefaultSelectBox';
import { YesNoOptions } from '../../../../constants/Options';

interface ProductMasterListViewProps {
    results: {
        results: ProductMaster[];
        previous: string | null;
        next: string | null;
    } | null;
    loading: boolean;
    error: string | null;
    setOnURL: React.Dispatch<React.SetStateAction<string>>;
    handleFormSuccess: () => void;
}

const CategoryMasterListView: React.FC<ProductMasterListViewProps> = ({ results, loading, error, handleFormSuccess, setOnURL }) => {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState<ProductMaster | null>(null);

    const columns = [
        { header: 'SN', accessor: 'sn', sortable: false },
        { header: 'Master Category Name', accessor: 'name', sortable: true },
        { header: 'Master Cate. Name(Nepali)', accessor: 'name_nep', sortable: false },
        { header: 'Des.', accessor: 'des', sortable: false },
        { header: 'Des.(Nepali)', accessor: 'des_nep', sortable: false },
        { header: 'Remarks', accessor: 'remarks', sortable: false },
        { header: 'Actions', accessor: 'actions', action: true },
    ];
    const modifiedData = useMemo(() => {
        if (!results) return [];
        return results.results.map((item: ProductMaster) => ({
            sn: item.sn,
            name: item.name,
            name_nep: item.name_nep,
            des: item.des,
            des_nep: item.des_nep,
            active: item.status,
            remarks: item.remarks,
            status: item.status
        }));
    }, [results]);

    const handleEdit = (item: ProductMaster) => {
        setCurrentItem(item);
        setDialogOpen(true);
    };

    const formik = useFormik({
        initialValues: {
            name: currentItem?.name || "",
            name_nep: currentItem?.name_nep || "",
            des: currentItem?.des || "",
            des_nep: currentItem?.des_nep || "",
            status: currentItem?.status === 1 ? "1" : "2" || "",
            remarks: currentItem?.remarks || "",
        },
        // validationSchema: readerEditValidationSchema,
        enableReinitialize: true,
        onSubmit: async (values, action) => {
            const formData = new FormData();
            formData.append("name", values.name);
            formData.append("name_nep", values.name_nep);
            formData.append("des", values.des);
            formData.append("des_nep", values.des_nep);
            formData.append("status", values.status === "2" ? "0" : "1");
            formData.append("remarks", values.remarks);
            try {
                await putDataToTheServer(AppURL.InventoryURL + 'inventory/product/master/retrieve/update/view/' + currentItem?.sn, formData).then((res) => {
                    if (res?.status === 200) {
                        handleFormSuccess();
                        setDialogOpen(false);
                        ToastSuccess({ message: res?.message });
                    } else if (res?.status === 400) {
                        ToastWarning({ message: res?.message });
                    }
                    else {
                        ToastWarning({ message: "Some Issue" });
                    }
                });
            } catch (error) {
                console.log(error);
            }
        },
    });

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return <p>Error loading data: {error}</p>;
    }

    return (
        <Fragment>
            <AppTable
                columns={columns}
                data={modifiedData}
                onEdit={handleEdit}
                onNext={() => results?.next && setOnURL(results.next)}
                onPrev={() => results?.previous && setOnURL(results.previous)}
            />
            {dialogOpen && currentItem && (
                <DialogBox open={dialogOpen} headerName='Master Category Edit Update' close={() => setDialogOpen(false)}>
                    <form onSubmit={formik.handleSubmit}>
                        <Grid container columnSpacing={3} rowSpacing={0.8}>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='name'
                                    label='Master Category Name'
                                    handleChange={formik.handleChange}
                                    value={formik.values.name}
                                    error={typeof formik.errors.name === 'string' ? formik.errors.name : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.name}
                                    required={true}
                                    placeholder='Enter Master Category Name'
                                />
                            </Grid>

                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Master Category Name(Nepali)'
                                    name='name_nep'
                                    value={formik.values.name_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'name_nep' })}
                                    required={true}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.name_nep === 'string' ? formik.errors.name_nep : undefined}
                                    placeholder='Enter Master Category(Nepali)'
                                    touched={formik.touched.name_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='des'
                                    label='Description'
                                    handleChange={formik.handleChange}
                                    value={formik.values.des}
                                    error={typeof formik.errors.des === 'string' ? formik.errors.des : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.des}
                                    required={false}
                                    placeholder='Enter Description'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <NepaliInputBox
                                    label='Des Name(Nepali)'
                                    name='des_nep'
                                    value={formik.values.des_nep}
                                    handleChange={(e) => handleNepaliInputChange(e, { setFieldValue: formik.setFieldValue, name: 'des_nep' })}
                                    required={false}
                                    handleBlur={formik.handleBlur}
                                    error={typeof formik.errors.des_nep === 'string' ? formik.errors.des_nep : undefined}
                                    placeholder='Enter Des. nepali'
                                    touched={formik.touched.des_nep}
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <InputTextBox
                                    type='text'
                                    name='remarks'
                                    label='Remarks'
                                    handleChange={formik.handleChange}
                                    value={formik.values.remarks}
                                    error={typeof formik.errors.remarks === 'string' ? formik.errors.remarks : undefined}
                                    handleBlur={formik.handleBlur}
                                    touched={formik.touched.remarks}
                                    required={false}
                                    placeholder='Enter Remarks'
                                />
                            </Grid>
                            <Grid item xl={6} lg={6} sm={6} md={6} xs={12}>
                                <DefaultSelectInputBox
                                    name="status"
                                    label='Active'
                                    disabled={false} // Adjust based on your needs
                                    options={YesNoOptions}
                                    value={formik.values.status}
                                    optionSelect={(name, selectedOption) => {
                                        formik.setFieldValue(name, selectedOption.value);
                                    }}
                                    required={true}
                                    placeholder="Select Options"
                                />
                            </Grid>

                            <Grid item xl={12} lg={12} sm={12} md={12} xs={12}>
                                <EndBaseLine top={18}>
                                    <Button btn_type='update' type='submit' el='button' />
                                </EndBaseLine>
                            </Grid>
                        </Grid>
                    </form>
                </DialogBox>
            )}
        </Fragment>
    );
};

export default CategoryMasterListView;
